import { cn, Spinner } from '@crm/ui';
import { ExternalLink, LogOut } from 'lucide-react';
import { Fragment } from 'react';
import { NavLink } from 'react-router-dom';
import { getInitials } from '@/lib/utils';
import { AccountBlock } from '@/modules/auth/components';
import { useLogout, useSession } from '@/modules/auth/hooks';
import { ACCOUNT_ITEMS, type NavSection, useNavSections, useOpenSection } from './navigation';

interface SidebarProps {
  onNavigate?: () => void;
  /** Recolhida: so os icones, como botoes. */
  collapsed?: boolean;
  /**
   * Mostra a conta (avatar, nome, perfil, preferencias e sair) no topo e no
   * rodape. Vale para a gaveta do celular, onde a barra do topo nao tem
   * espaco para o menu do avatar.
   */
  withAccount?: boolean;
  /**
   * Submenu embaixo do menu, dentro da propria barra. E assim na gaveta do
   * celular; no desktop ele sai daqui e vira a coluna sobreposta.
   */
  inlineSubmenu?: boolean;
}

/**
 * Submenu do desktop: a segunda coluna, colada na barra e por cima da tela.
 * Fica sempre montado para poder animar a saida; fechado, ele nao recebe
 * clique nenhum.
 */
export function SectionFlyout({
  section,
  isOpen,
  onNavigate,
  className,
  style,
}: {
  section?: NavSection;
  isOpen: boolean;
  onNavigate: (to: string) => void;
  className?: string;
  /** A coluna comeca onde a barra termina - e ela muda quando recolhida. */
  style?: React.CSSProperties;
}) {
  return (
    <div
      data-nav-root
      aria-hidden={!isOpen}
      style={style}
      className={cn(
        // A altura e o topo chegam pelo style: quem manda e a barra do topo.
        'fixed z-30 hidden w-64 overflow-y-auto border-r border-border bg-background shadow-xl lg:block',
        'transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none',
        isOpen ? 'translate-x-0 opacity-100' : 'pointer-events-none -translate-x-2 opacity-0',
        className,
      )}
    >
      {section ? (
        <>
          {/* Mesma altura dos blocos do menu ao lado: o nome da secao fica
              na linha do primeiro icone, e nao espremido contra o topo. */}
          <p className="flex h-16 items-center border-b border-border px-4 text-sm font-semibold text-foreground">
            {section.title}
          </p>

          {section.items.map((item, indice) => (
            <Fragment key={item.to}>
              {item.group && item.group !== section.items[indice - 1]?.group ? (
                <p className="px-4 pb-1 pt-4 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {item.group}
                </p>
              ) : null}

              {item.external ? (
                // Tela fora do sistema: abrir em outra aba deixa o menu onde
                // estava, e olhar a pagina publica nao e sair do trabalho.
                <a
                  href={item.to}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => onNavigate(item.to)}
                  className="flex h-12 items-center gap-3 border-l-[3px] border-transparent pl-3 pr-2 text-sm font-medium text-slate-800 transition-colors hover:bg-black/[0.05] hover:text-slate-900 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white"
                >
                  <item.icon
                    className="h-5 w-5 shrink-0 text-slate-700 dark:text-slate-300"
                    aria-hidden
                  />
                  <span className="truncate">{item.label}</span>
                  <ExternalLink className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                </a>
              ) : (
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={() => onNavigate(item.to)}
                  className={({ isActive }) =>
                    cn(
                      'flex h-12 items-center gap-3 border-l-[3px] pl-3 pr-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'border-primary bg-primary/10 font-semibold text-primary'
                        : 'border-transparent text-slate-800 hover:bg-black/[0.05] hover:text-slate-900 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white',
                    )
                  }
                >
                  <item.icon
                    className="h-5 w-5 shrink-0 text-slate-700 dark:text-slate-300"
                    aria-hidden
                  />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              )}
            </Fragment>
          ))}
        </>
      ) : null}
    </div>
  );
}

/**
 * Navegacao lateral da area autenticada, filtrada pelas permissoes.
 *
 * Cada menu e um bloco: icone grande, nome embaixo e, quando aberto, os
 * submenus em lista sob uma guia vertical. Uma secao por vez - com tudo
 * aberto a lista passa da tela no celular.
 */
export function Sidebar({
  onNavigate,
  collapsed = false,
  withAccount = false,
  inlineSubmenu = true,
}: SidebarProps) {
  const allSections = useNavSections();
  const { sections, openSection, activeSection, toggleSection, closeAfterNavigate } =
    useOpenSection();
  const { user } = useSession();
  const logout = useLogout();

  // Recolhida no desktop, a barra vira uma trilha de icones sem secoes.
  if (collapsed) {
    return (
      <nav className="flex flex-col items-center gap-1 p-2" aria-label="Navegacao principal">
        {allSections.flatMap((section) =>
          section.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              title={item.label}
              aria-label={item.label}
              className={({ isActive }) =>
                cn(
                  'flex h-11 w-11 items-center justify-center transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-800 hover:bg-primary/10 hover:text-primary dark:text-slate-300',
                )
              }
            >
              <item.icon className="h-6 w-6" />
            </NavLink>
          )),
        )}
      </nav>
    );
  }

  return (
    <nav className="flex min-h-full flex-col" aria-label="Navegacao principal">
      {/* No celular a conta mora aqui: a barra do topo fica so com a busca. */}
      {withAccount && user ? (
        <div className="flex items-center gap-3 border-b border-border px-4 py-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
            {getInitials(user.name)}
          </span>
          <span className="min-w-0">
            <span className="block truncate font-medium">{user.name}</span>
            <span className="block truncate text-sm text-muted-foreground">{user.email}</span>
          </span>
        </div>
      ) : null}

      {sections.map((section) => {
        const isOpen = openSection === section.title;
        // A secao da tela atual continua marcada com a barra mesmo depois de
        // fechar o submenu - e o unico sinal de onde se esta.
        const isCurrent = activeSection === section.title;

        // Secao que e destino (o painel): o bloco vira link e nao abre nada.
        if (section.to) {
          return (
            <div key={section.title} className="border-b border-border">
              <NavLink
                to={section.to}
                end={section.end}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'flex w-full flex-col items-center gap-2 border-l-[3px] px-2 py-4 transition-colors',
                    isActive
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-transparent bg-white/55 text-slate-700 shadow-[0_1px_2px_0_rgba(15,23,42,0.06)] hover:bg-white hover:text-slate-900 dark:bg-white/[0.04] dark:text-slate-300 dark:shadow-none dark:hover:bg-white/10 dark:hover:text-white',
                  )
                }
              >
                <section.icon className="h-9 w-9 text-current" aria-hidden />
                <span className="w-full truncate text-center text-[13px] font-semibold">
                  {section.title}
                </span>
              </NavLink>
            </div>
          );
        }

        return (
          // A divisoria e mais forte que a borda padrao: sobre o prateado, a
          // linha clara sumia e os blocos viravam um so.
          <div key={section.title} className="border-b border-border">
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => toggleSection(section.title)}
              className={cn(
                // A guia na cor primaria a esquerda marca onde se esta. O
                // hover e cinza de proposito: quando ele tambem era violeta,
                // passar o mouse parecia selecionar, e a marca de "estou
                // aqui" deixava de valer.
                'flex w-full flex-col items-center gap-2 border-l-[3px] px-2 py-4 transition-colors',
                isOpen
                  ? // Aberto: a borda da direita liga o bloco a coluna que
                    // abriu ao lado.
                    'border-primary bg-primary/15 text-primary shadow-[inset_-2px_0_0_0_var(--color-primary)]'
                  : isCurrent
                    ? 'border-primary bg-primary/10 text-primary'
                    : // Fechado nao e "nada": ganha a propria superficie, um
                      // degrau acima do prateado da barra. Assim o bloco se le
                      // como tecla mesmo sem estar selecionado.
                      'border-transparent bg-white/55 text-slate-700 shadow-[0_1px_2px_0_rgba(15,23,42,0.06)] hover:bg-white hover:text-slate-900 dark:bg-white/[0.04] dark:text-slate-300 dark:shadow-none dark:hover:bg-white/10 dark:hover:text-white',
              )}
            >
              <section.icon
                // Cinza no cinza sumia: fora da secao ativa o icone usa a cor
                // do texto, so um pouco mais leve.
                className={cn(
                  'h-9 w-9',
                  isOpen || isCurrent ? 'text-primary' : 'text-slate-700 dark:text-slate-200',
                )}
                aria-hidden
              />
              {/* Barra estreita: nome em uma linha so, sem quebrar. */}
              <span className="w-full truncate text-center text-[13px] font-semibold">
                {section.title}
              </span>
            </button>

            {/* A guia comeca na borda do bloco; no item aberto ela vira a
                marca da tela atual, na cor primaria. */}
            {isOpen && inlineSubmenu ? (
              <div className="border-l border-border">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => {
                      closeAfterNavigate(item.to);
                      onNavigate?.();
                    }}
                    className={({ isActive }) =>
                      cn(
                        '-ml-px flex h-12 items-center gap-3 border-l-[3px] pl-3 pr-2 text-sm font-medium transition-colors',
                        // Barra e fundo mais leves que os do menu: o submenu
                        // esta um nivel abaixo na hierarquia.
                        isActive
                          ? 'border-primary/60 bg-primary/10 font-semibold text-primary'
                          : 'border-transparent text-slate-800 hover:bg-black/[0.05] hover:text-slate-900 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white',
                      )
                    }
                  >
                    <item.icon
                      className="h-5 w-5 shrink-0 text-slate-700 dark:text-slate-300"
                      aria-hidden
                    />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}

      {withAccount ? (
        <div className="mt-auto border-t border-border py-2">
          {user ? (
            <div className="border-b border-border pb-2">
              <AccountBlock user={user} />
            </div>
          ) : null}

          {ACCOUNT_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex h-12 items-center gap-3 px-4 text-base transition-colors',
                  isActive
                    ? 'bg-primary/10 font-medium text-primary'
                    : 'text-foreground hover:bg-muted',
                )
              }
            >
              <item.icon className="h-5 w-5 shrink-0 text-muted-foreground" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}

          <button
            type="button"
            disabled={logout.isPending}
            onClick={() => logout.mutate()}
            className="flex h-12 w-full items-center gap-3 px-4 text-base text-destructive transition-colors hover:bg-muted"
          >
            {logout.isPending ? (
              <Spinner className="h-5 w-5" />
            ) : (
              <LogOut className="h-5 w-5 shrink-0" />
            )}
            Sair
          </button>
        </div>
      ) : null}
    </nav>
  );
}
